import { intersection } from 'lodash-es';
import { PROPERTY_TYPE_MULTI_SELECT, PROPERTY_TYPE_SELECT, PROPERTY_TYPE_STATUS } from 'shared/services/notion-constants';

// Pure functions describing how a Notion database schema becomes a Notix
// template, extracted from template.js so they can be tested directly. They run
// on every template load, so a throw here takes the whole template list down.

const extractOptions = (property) => {
  if (!['select', 'status', 'multi_select'].includes(property.type)) {
    return [];
  }
  // A partial payload has no options object. Returning none is recoverable;
  // throwing here fails the whole template list.
  const config = property[property.type];
  if (config === undefined || !Array.isArray(config.options)) {
    return [];
  }

  return config.options.map((x) => ({ text: x.name, value: x.name, color: x.color }));
};

const getPrefill = (property) => {
  if (property.type === 'url') {
    return 'url';
  }

  return 'manual';
};

export const parseProperties = (properties) => {
  const res = {};
  const possibleProperties = ['title', 'rich_text', 'number', 'select', 'multi_select', 'date', 'url', 'checkbox', 'email', 'phone_number', 'status'];

  Object.keys(properties).forEach((propertyKey) => {
    const property = properties[propertyKey];
    const options = extractOptions(property);
    if (possibleProperties.includes(property.type)) {
      const shared = {
        id: property.id,
        type: property.type,
        options,
        prefill: getPrefill(property),
        query: '',
        predefined: [],
        checked: false,
        numTime: 0,
        unityTime: 'day',
        signTime: 1,
        endTime: false,
      };
      res[property.id] = { ...shared, name: property.name, endDate: { ...shared, name: `${property.name} - end` } };
    }
  });
  return res;
};

export const writeDefaultTemplate = (database) => ({
  database,
  name: `Template for ${database.name}`,
  properties: parseProperties(database.properties),
  blocks: [],
});

export const updateProperties = (oldProperties, database) => {
  const oldPs = JSON.parse(JSON.stringify(oldProperties));
  const newPs = parseProperties(database.properties);
  const updatedPs = {};

  Object.keys(newPs).forEach((key) => {
    const old = oldPs[key];
    if (old === undefined) {
      updatedPs[key] = newPs[key];
      return;
    }

    old.name = newPs[key].name;
    old.type = newPs[key].type;
    old.options = newPs[key].options;
    // Templates saved before end dates existed have no endDate at all, and this
    // runs on every load - so an old template would break the whole list.
    if (old.endDate === undefined) {
      old.endDate = newPs[key].endDate;
    } else {
      old.endDate.name = newPs[key].endDate.name;
      old.endDate.type = newPs[key].endDate.type;
      old.endDate.options = newPs[key].endDate.options;
    }
    if ([PROPERTY_TYPE_MULTI_SELECT, PROPERTY_TYPE_SELECT, PROPERTY_TYPE_STATUS].includes(newPs[key].type)) {
      old.predefined = intersection(old.predefined, old.options.map((x) => x.value));
    }
    updatedPs[key] = old;
  });

  return updatedPs;
};
